/**
 * Улучшенная функция фильтрации номенклатуры по поисковому запросу.
 *
 * Стратегия поиска:
 * 1. Сначала ищем точное совпадение фразы (substring match)
 * 2. Если ничего не найдено - разделяем на слова и ищем позиции,
 *    где присутствуют ВСЕ слова из запроса (в любом порядке)
 *
 * Пример:
 * Запрос: "сверло шлиф"
 * Найдет: "сверло ц/x ф21 шлиф" (оба слова присутствуют)
 * Не найдет: "сверло ц/x ф21" (нет слова "шлиф")
 */

/**
 * Фильтрует список номенклатуры по поисковому запросу.
 *
 * @param {Array<string>} items - Список номенклатуры для фильтрации
 * @param {string} searchTerm - Поисковый запрос
 * @returns {Array<string>} - Отфильтрованный список
 */
export function filterNomenclature(items, searchTerm) {
  // Если запрос пустой - вернуть все элементы
  if (!searchTerm || searchTerm.trim() === '') {
    return items;
  }

  const searchLower = searchTerm.toLowerCase().trim();

  // СТАДИЯ 1: Попытка точного совпадения фразы
  const exactMatches = items.filter(item =>
    item.toLowerCase().includes(searchLower)
  );

  // Если нашли точные совпадения - вернуть их
  if (exactMatches.length > 0) {
    return exactMatches;
  }

  // СТАДИЯ 2: Поиск по отдельным словам (fallback)
  // Разделяем запрос на слова (по пробелам, знакам препинания)
  const words = searchLower
    .split(/[\s,;.:/\\()\[\]{}]+/)  // Разделители: пробелы и знаки препинания
    .filter(word => word.length > 0);  // Убираем пустые слова

  // Если слов нет или только одно слово (уже искали выше) - вернуть пустой результат
  if (words.length === 0 || words.length === 1) {
    return [];
  }

  // Фильтруем позиции, где присутствуют ВСЕ слова из запроса
  const wordMatches = items.filter(item => {
    const itemLower = item.toLowerCase();
    // Проверяем, что ВСЕ слова присутствуют в названии
    return words.every(word => itemLower.includes(word));
  });

  return wordMatches;
}


/**
 * Подсчитывает количество найденных позиций (для отображения счетчика).
 *
 * @param {Array<string>} items - Список номенклатуры
 * @param {string} searchTerm - Поисковый запрос
 * @returns {number} - Количество найденных позиций
 */
export function countMatches(items, searchTerm) {
  return filterNomenclature(items, searchTerm).length;
}


/**
 * Получает информацию о стратегии поиска (для отладки/логирования).
 *
 * @param {Array<string>} items - Список номенклатуры
 * @param {string} searchTerm - Поисковый запрос
 * @returns {Object} - Информация о результатах поиска
 */
export function getSearchInfo(items, searchTerm) {
  if (!searchTerm || searchTerm.trim() === '') {
    return {
      strategy: 'none',
      count: items.length,
      words: []
    };
  }

  const searchLower = searchTerm.toLowerCase().trim();

  // Точные совпадения
  const exactMatches = items.filter(item =>
    item.toLowerCase().includes(searchLower)
  );

  if (exactMatches.length > 0) {
    return {
      strategy: 'exact',
      count: exactMatches.length,
      words: [searchLower]
    };
  }

  // Поиск по словам
  const words = searchLower
    .split(/[\s,;.:/\\()\[\]{}]+/)
    .filter(word => word.length > 0);

  if (words.length <= 1) {
    return {
      strategy: 'exact',
      count: 0,
      words: []
    };
  }

  const wordMatches = items.filter(item => {
    const itemLower = item.toLowerCase();
    return words.every(word => itemLower.includes(word));
  });

  return {
    strategy: 'words',
    count: wordMatches.length,
    words: words
  };
}
